import { Link, useNavigate, useParams } from 'react-router-dom';
import { listByMonth } from '../../db/entries';
import { getCategoryMap, resolveCategory } from '../../db/categories';
import { emptyProjects, getProjectsDoc } from '../../db/projects';
import { useAsync } from '../hooks/useAsync';
import { useDataVersion } from '../../db/events';
import { useBreakpoint } from '../hooks/useBreakpoint';
import {
  addMonths,
  calendarGrid,
  formatMonthJa,
  formatTime,
  isToday,
  monthKeyToDate,
  toDateKey,
  toMonthKey,
} from '../../lib/datetime';
import type { Category, ProjectsDoc } from '../../domain/types';
import { EntryBadge } from '../components/EntryBadge';
import { MediaImage } from '../components/MediaImage';

const WEEKDAY = ['月', '火', '水', '木', '金', '土', '日'];

export function MonthView({ monthKey }: { monthKey?: string } = {}) {
  const version = useDataVersion();
  const params = useParams();
  const month = monthKey ?? params.month ?? toMonthKey(new Date());
  const navigate = useNavigate();
  const bp = useBreakpoint();
  const monthDate = monthKeyToDate(month);

  const entries = useAsync(() => listByMonth(month), [month, version], []);
  const categories = useAsync<Map<string, Category>>(getCategoryMap, [version], new Map());
  const projects = useAsync<ProjectsDoc>(getProjectsDoc, [version], emptyProjects());

  const photoCount = entries.reduce((n, e) => n + e.media.length, 0);
  // その月の中なら今日、範囲外ならその月の1日に対して作る
  const now = new Date();
  const defaultNewDate = toMonthKey(now) === month ? now : monthDate;

  const head = (
    <header className="view-head">
      <button
        className="nav-btn"
        onClick={() => navigate(`/month/${toMonthKey(addMonths(monthDate, -1))}`)}
        aria-label="前の月"
      >
        ‹
      </button>
      <div className="view-title">
        <h1>{formatMonthJa(monthDate)}</h1>
        <span className="summary">
          {entries.length}件の記録 / {photoCount}枚の写真
        </span>
      </div>
      <button
        className="nav-btn"
        onClick={() => navigate(`/month/${toMonthKey(addMonths(monthDate, 1))}`)}
        aria-label="次の月"
      >
        ›
      </button>
      <button className="today-btn" onClick={() => navigate(`/month/${toMonthKey(new Date())}`)}>
        今月
      </button>
      <Link className="primary-btn head-new" to={`/new?date=${toDateKey(defaultNewDate)}`}>
        ＋ 新規
      </Link>
    </header>
  );

  // スマホはカレンダー格子（仕様書 §12.5）
  if (bp === 'phone') {
    const byDay = new Map<string, typeof entries>();
    for (const e of entries) {
      const list = byDay.get(e.dateKey) ?? [];
      list.push(e);
      byDay.set(e.dateKey, list);
    }
    const cells = calendarGrid(monthDate);

    return (
      <div className="view">
        {head}
        <div className="view-body">
          <div className="cal">
            {WEEKDAY.map((w) => (
              <div key={w} className="cal-dow">
                {w}
              </div>
            ))}
            {cells.map((d) => {
              const key = toDateKey(d);
              const list = byDay.get(key) ?? [];
              const outside = toMonthKey(d) !== month;
              return (
                <button
                  key={key}
                  className={`cal-cell${outside ? ' is-outside' : ''}${isToday(d) ? ' is-today' : ''}`}
                  onClick={() => navigate(`/day/${key}`)}
                >
                  <span className="cal-num">{d.getDate()}</span>
                  <span className="cal-dots">
                    {list.slice(0, 3).map((e) => (
                      <i
                        key={e.id}
                        className="cal-dot"
                        style={{ background: resolveCategory(categories, e.categoryId).color }}
                      />
                    ))}
                    {list.length > 3 && <i className="cal-more">…</i>}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // タブレット / PC はタイトルと画像のグリッド（仕様書 §12.5）
  return (
    <div className="view">
      {head}
      <div className="view-body">
        {entries.length === 0 ? (
          <div className="empty">
            <p>この月の記録はありません</p>
          </div>
        ) : (
          <div className="month-grid">
            {entries.map((e) => {
              const cat = resolveCategory(categories, e.categoryId);
              const cover = e.media[0];
              return (
                <Link key={e.id} to={`/entry/${e.id}`} className="month-card">
                  {cover ? (
                    <MediaImage media={cover} month={e.monthKey} entryId={e.id} className="month-cover" />
                  ) : (
                    <div className="month-cover" style={{ background: cat.color }} />
                  )}
                  <div className="month-card-body">
                    <div className="month-card-meta">
                      {e.dateKey.slice(5).replace('-', '/')} {formatTime(e.date)}
                      <EntryBadge categoryId={e.categoryId} projectId={e.projectId} categories={categories} projects={projects} />
                    </div>
                    <div className="month-card-title">{e.title || '（無題）'}</div>
                    <div className="month-card-excerpt">{e.body}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
